/**
 * Block-level token renderer.
 * Handles: headings, paragraphs, code, tables, lists, blockquotes, hr, html, space.
 * Recursively renders nested blocks (list items, blockquotes).
 */
import React from 'react';
import { decode } from 'html-entities';
import MarkdownInlineTokens from './MarkdownInlineTokens';
import CodeBlock from './CodeBlock';

const MarkdownTokens = ({ tokens = [], top = true }) => {
  if (!tokens || tokens.length === 0) return null;

  return tokens.map((token, idx) => {
    switch (token.type) {
      case 'heading': {
        const Tag = `h${token.depth}`;
        return (
          <Tag key={idx}>
            <MarkdownInlineTokens tokens={token.tokens} />
          </Tag>
        );
      }

      case 'paragraph':
        return (
          <p key={idx} style={{ whiteSpace: 'pre-wrap' }}>
            <MarkdownInlineTokens tokens={token.tokens || []} />
          </p>
        );

      case 'text':
        if (top) {
          return (
            <p key={idx}>
              {token.tokens ? (
                <MarkdownInlineTokens tokens={token.tokens} />
              ) : (
                token.text
              )}
            </p>
          );
        }
        return token.tokens ? (
          <MarkdownInlineTokens key={idx} tokens={token.tokens} />
        ) : (
          <React.Fragment key={idx}>{decode(token.text)}</React.Fragment>
        );

      case 'code':
        return <CodeBlock key={idx} code={token.text} language={token.lang || ''} />;

      case 'table':
        return (
          <div key={idx} className="not-prose overflow-x-auto my-2 rounded-lg border border-gray-200 dark:border-gray-700" style={{ WebkitOverflowScrolling: 'touch' }}>
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  {token.header.map((cell, i) => (
                    <th key={i} className="px-3 py-2 text-left font-semibold text-gray-900 dark:text-gray-100 border-b border-r border-gray-200 dark:border-gray-700 last:border-r-0 whitespace-nowrap" style={token.align[i] ? { textAlign: token.align[i] } : undefined}>
                      <MarkdownInlineTokens tokens={cell.tokens} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {token.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-3 py-2 text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-800 last:border-r-0" style={token.align[ci] ? { textAlign: token.align[ci] } : undefined}>
                        <MarkdownInlineTokens tokens={cell.tokens} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'list': {
        // REGEX HACK by Claude — hide browser marker when it would duplicate AI's own marker.
        // Checks raw text: "- 1. text" = bullet wrapping number → hide bullet.
        // Checks item tokens: if item contains a nested list as only content → hide outer marker.
        // TODO: Replace with non-regex detection.
        const Tag = token.ordered ? 'ol' : 'ul';
        const items = token.items || [];
        const anyItemHasNestedList = items.some(item => (item.tokens || []).some(t => t.type === 'list'));
        const hideMarker = !token.ordered && anyItemHasNestedList;

        return (
          <Tag key={idx}
            start={token.ordered ? (token.start || 1) : undefined}
            style={hideMarker ? { listStyleType: 'none', paddingLeft: '1em' } : undefined}
          >
            {token.items.map((item, i) => (
              <li key={i}>
                <MarkdownTokens tokens={item.tokens} top={token.loose} />
              </li>
            ))}
          </Tag>
        );
      }

      case 'blockquote':
        return (
          <blockquote key={idx}>
            <MarkdownTokens tokens={token.tokens} />
          </blockquote>
        );

      case 'hr':
        return <hr key={idx} />;

      case 'html':
        return <div key={idx} dangerouslySetInnerHTML={{ __html: token.text }} />;

      case 'space':
        return <div key={idx} className="md-space" />;

      default:
        return null;
    }
  });
};

export default MarkdownTokens;
